import styled from "styled-components";
import { SelectedItem, HomeworkSchedule } from "shared/types/Report";
import { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import Modal from "shared/components/Modal";

interface HomeworkDateScheduleModalProps {
    isOpen: boolean;
    selectedItems: SelectedItem[];
    schedules: HomeworkSchedule[];
    onClose: () => void;
    onConfirm: (schedules: HomeworkSchedule[]) => void;
}

const HomeworkDateScheduleModal = ({ isOpen, selectedItems, schedules, onClose, onConfirm }: HomeworkDateScheduleModalProps) => {
    const [localSchedules, setLocalSchedules] = useState<HomeworkSchedule[]>([]);
    const [isErrorModal, setIsErrorModal] = useState(false);

    function parseName(name: string) {
        return name.match(/([a-zA-Z가-힣\s]+|\d+)/g)?.map(part => {
            const num = Number(part);
            return isNaN(num) ? part : num;
        }) ?? [name];
    }
    function compareNames(a: string, b: string) {
        const aParts = parseName(a);
        const bParts = parseName(b);
        const len = Math.max(aParts.length, bParts.length);
        for (let i = 0; i < len; i++) {
            const aVal = aParts[i];
            const bVal = bParts[i];
            if (aVal === undefined) return -1;
            if (bVal === undefined) return 1;
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                if (aVal !== bVal) return aVal - bVal;
            } else if (typeof aVal === 'string' && typeof bVal === 'string') {
                if (aVal !== bVal) return aVal.localeCompare(bVal);
            } else {
                return typeof aVal === 'number' ? -1 : 1;
            }
        }
        return new Blob([a]).size - new Blob([b]).size;
    }

    useEffect(() => {
        if (isOpen) {
            if (schedules.length > 0) {
                // 이미 저장된 스케줄이 있으면 그대로 사용
                setLocalSchedules(schedules.map(s => ({ ...s })));
            } else {
                // 없으면 초기화
                const sortedItems = [...selectedItems].sort((a, b) => compareNames(a.name, b.name));
                setLocalSchedules(
                    sortedItems.map((item, idx) => ({
                        id: item.id,
                        dayOfWeek: new Array(7).fill(false),
                        order: idx,
                        interval: 1,
                        dates: [""]
                    }))
                );
            }
        }
    }, [isOpen, selectedItems, schedules]);

    if (!isOpen) return null;

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const items = Array.from(localSchedules);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        const updatedSchedules = items.map((item, index) => ({
            ...item,
            order: index
        }));
        setLocalSchedules(updatedSchedules);
    };

    const handleDateChange = (scheduleIdx: number, dateIdx: number, value: string) => {
        setLocalSchedules(prev => prev.map((s, i) =>
            i === scheduleIdx
                ? { ...s, dates: s.dates ? s.dates.map((d, j) => j === dateIdx ? value : d) : [value] }
                : s
        ));
    };

    const handleAddDate = (scheduleIdx: number) => {
        setLocalSchedules(prev => prev.map((s, i) =>
            i === scheduleIdx
                ? { ...s, dates: s.dates ? [...s.dates, ""] : [""] }
                : s
        ));
    };

    const handleRemoveDate = (scheduleIdx: number, dateIdx: number) => {
        setLocalSchedules(prev => prev.map((s, i) =>
            i === scheduleIdx
                ? { ...s, dates: s.dates ? s.dates.filter((_, j) => j !== dateIdx) : [] }
                : s
        ));
    };

    const handleConfirm = () => {
        const hasAllDates = localSchedules.every(s => Array.isArray(s.dates) && s.dates.length > 0 && s.dates.every(d => d));
        if (!hasAllDates) {
            setIsErrorModal(true);
            return;
        }
        onConfirm(localSchedules);
        onClose();
    };

    return (
        <Overlay>
            <ModalBox>
                <Header>
                    <Title>날짜 지정 일정 관리</Title>
                    <CloseButton onClick={onClose}>×</CloseButton>
                </Header>
                <Content>
                    <InfoText>각 과제별로 여러 날짜를 직접 지정할 수 있습니다.<br/>과제를 드래그 하시면 과제 순서를 변경하실 수 있습니다.</InfoText>
                    {localSchedules.length === 0 ? (
                        <InfoText>선택된 과제가 없습니다.</InfoText>
                    ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="dateSchedules">
                                {(provided) => (
                                    <ScheduleList {...provided.droppableProps} ref={provided.innerRef}>
                                        {localSchedules.map((schedule, idx) => {
                                            const item = selectedItems.find(item => item.id === schedule.id);
                                            if (!item) return null;
                                            return (
                                                <Draggable key={schedule.id} draggableId={schedule.id} index={idx}>
                                                    {(provided) => (
                                                        <ScheduleItem ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                            <ItemName>{item.name}</ItemName>
                                                            <DateList>
                                                                {(schedule.dates || [""]).map((date, dIdx) => (
                                                                    <DateInputWrap key={dIdx}>
                                                                        <DateInput
                                                                            type="date"
                                                                            value={date}
                                                                            onChange={e => handleDateChange(idx, dIdx, e.target.value)}
                                                                        />
                                                                        <RemoveDateBtn onClick={() => handleRemoveDate(idx, dIdx)}>삭제</RemoveDateBtn>
                                                                    </DateInputWrap>
                                                                ))}
                                                                <AddDateBtn onClick={() => handleAddDate(idx)}>+ 날짜 추가</AddDateBtn>
                                                            </DateList>
                                                        </ScheduleItem>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </ScheduleList>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </Content>
                <Footer>
                    <FooterButton onClick={onClose}>취소</FooterButton>
                    <FooterButtonPrimary onClick={handleConfirm}>확인</FooterButtonPrimary>
                </Footer>
            </ModalBox>
            <Modal isOpen={isErrorModal} title="오류" content="모든 과제에 날짜를 1개 이상 지정해야 합니다." onClose={() => setIsErrorModal(false)} />
        </Overlay>
    );
};

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
`;

const ModalBox = styled.div`
    background: white;
    min-width: 700px;
    max-width: 900px;
    padding: 32px 32px 24px 32px;
    border-radius: 16px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.12);
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const Title = styled.h2`
    font-size: 22px;
    font-weight: 700;
    color: #1E293B;
    margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 28px;
    color: #A0AEC0;
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
        color: #FD0000;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const InfoText = styled.div`
    color: #475569;
    font-size: 15px;
    margin-bottom: 8px;
    font-weight: 500;
`;

const ScheduleList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    padding-right: 12px;
    max-height: 400px;
`;

const ScheduleItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 24px;
    padding: 20px 32px;
    background: #F8FAFC;
    border-radius: 10px;
    border: 1px solid #E2E8F0;
`;

const ItemName = styled.span`
    font-size: 15px;
    color: #2D3748;
    min-width: 120px;
`;

const DateList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DateInputWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const DateInput = styled.input`
    padding: 8px 12px;
    border: 1px solid #E2E8F0;
    font-size: 14px;
    border-radius: 8px;
    color: #2D3748;
    transition: all 0.2s;
    &:focus {
        outline: none;
        border-color: #3182CE;
        box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
    }
`;

const RemoveDateBtn = styled.button`
    background: #F87171;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 13px;
    cursor: pointer;
    &:hover {
        background: #DC2626;
    }
`;

const AddDateBtn = styled.button`
    background: #3182CE;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 4px 12px;
    font-size: 13px;
    cursor: pointer;
    margin-top: 4px;
    &:hover {
        background: #2563EB;
    }
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
`;

const FooterButton = styled.button`
    padding: 10px 28px;
    background: white;
    color: #3182CE;
    border: 1.5px solid #3182CE;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
        background: #F0F8FF;
    }
`;

const FooterButtonPrimary = styled.button`
    padding: 10px 28px;
    background: #3182CE;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
        background: #2563EB;
    }
`;

export default HomeworkDateScheduleModal; 